// frontend/src/pages/TestPage.jsx - Updated with skip logic for existing photos

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { showError, showInfo } from '../utils/toastUtils';
import DemographicsForm from '../components/DemographicsForm';
import SampleVideo from '../components/SampleVideo';
import ManualFaceCapture from '../components/ManualFaceCapture';
import TestInterface from '../components/TestInterface';
import FaceVerification from '../components/FaceVerification';
import backgroundPhotoService from '../services/backgroundPhotoService';
import api from '../services/api';
import { startSession, submitAssessment, getProgress } from '../services/api';
import { getTranslation } from '../i18n/translations';
import { useLanguage } from '../contexts/LanguageContext'

const TestPage = () => {
    const { accessToken } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [testData, setTestData] = useState(null);
    const [showDemographics, setShowDemographics] = useState(true);
    const [showSampleVideo, setShowSampleVideo] = useState(false);
    const [showTest, setShowTest] = useState(false);
    const [demographicsCompleted, setDemographicsCompleted] = useState(false);
    const [faceVerificationEnabled, setFaceVerificationEnabled] = useState(true);
    const [assessmentType, setAssessmentType] = useState('MIPS');
    const [assessmentId, setAssessmentId] = useState('MIPS-00001');
    const [formId, setFormId] = useState(null);
    const [savedProgress, setSavedProgress] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [language, setLanguageLocal] = useState('en');
    const { setLanguage } = useLanguage();

    const t = (key, replacements) => getTranslation(language, key, replacements);

    // Face Photo States
    const [facePhotosTaken, setFacePhotosTaken] = useState({
        1: false,
        2: false,
        3: false
    });
    const [photoCaptureInProgress, setPhotoCaptureInProgress] = useState({
        1: false,
        2: false,
        3: false
    });
    const [middlePhotoTriggered, setMiddlePhotoTriggered] = useState(false);

    // Manual Photo 1 State
    const [showManualPhotoCapture, setShowManualPhotoCapture] = useState(false);

    // Sample Video States
    const [sampleVideoData, setSampleVideoData] = useState({
        url: null,
        required: false,
        watched: false
    });
    const [videoCompleted, setVideoCompleted] = useState(false);

    useEffect(() => {
        if (accessToken) {
            checkSessionAndProceed();
            // Request camera permission early for background captures
            requestCameraPermission();
        }
    }, [accessToken]);

    // ============================================
    // REQUEST CAMERA PERMISSION EARLY
    // ============================================
    const requestCameraPermission = async () => {
        try {
            console.log('📸 Requesting camera permission early...');
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'user',
                    width: { ideal: 320 },
                    height: { ideal: 240 }
                },
                audio: false
            });
            stream.getTracks().forEach(track => track.stop());
            console.log('✅ Camera permission granted');
        } catch (error) {
            console.warn('⚠️ Camera permission not granted:', error.message);
        }
    };

    // ============================================
    // CHECK PHOTO STATUS BEFORE CAPTURE
    // ============================================
    const checkPhotoStatus = async (photoOrder) => {
        try {
            const statusResponse = await api.get(`/face-photos/${accessToken}/status`);
            if (statusResponse.data.success) {
                const status = statusResponse.data.status;
                const columnName = `face_photo_${photoOrder}_taken`;
                return status[columnName] || false;
            }
            return false;
        } catch (error) {
            console.log('Could not fetch photo status:', error.message);
            return false;
        }
    };

    // ============================================
    // CAPTURE PHOTO 1 (Before Assessment) - SKIP IF ALREADY TAKEN
    // ============================================
    const capturePhoto1 = async () => {
        // Check if already taken
        const isTaken = await checkPhotoStatus(1);
        if (isTaken) {
            console.log('📸 Photo 1 already taken, skipping...');
            setFacePhotosTaken(prev => ({ ...prev, 1: true }));
            return;
        }

        if (photoCaptureInProgress[1]) {
            console.log('📸 Photo 1 capture already in progress');
            return;
        }

        try {
            setPhotoCaptureInProgress(prev => ({ ...prev, 1: true }));
            console.log('📸 Capturing photo 1 in background...');

            const result = await backgroundPhotoService.capturePhoto1(accessToken);

            if (result && result.success) {
                setFacePhotosTaken(prev => ({ ...prev, 1: true }));
                console.log('✅ Photo 1 captured successfully');
            } else {
                console.error('❌ Photo 1 capture failed:', result?.error);
            }
        } catch (error) {
            console.error('❌ Failed to capture photo 1:', error);
        } finally {
            setPhotoCaptureInProgress(prev => ({ ...prev, 1: false }));
        }
    };

    // ============================================
    // CAPTURE PHOTO 2 (Middle of Assessment) - SKIP IF ALREADY TAKEN
    // ============================================
    const capturePhoto2 = async () => {
        // Check if already taken
        const isTaken = await checkPhotoStatus(2);
        if (isTaken) {
            console.log('📸 Photo 2 already taken, skipping...');
            setFacePhotosTaken(prev => ({ ...prev, 2: true }));
            setMiddlePhotoTriggered(true);
            return;
        }

        if (photoCaptureInProgress[2] || middlePhotoTriggered) {
            console.log('📸 Photo 2 already in progress or triggered');
            return;
        }

        try {
            setMiddlePhotoTriggered(true);
            setPhotoCaptureInProgress(prev => ({ ...prev, 2: true }));
            console.log('📸 Capturing photo 2 in background...');

            showInfo('📸 Taking verification photo...');

            const result = await backgroundPhotoService.capturePhoto2(accessToken);

            if (result && result.success) {
                setFacePhotosTaken(prev => ({ ...prev, 2: true }));
                console.log('✅ Photo 2 captured successfully');
            } else {
                console.error('❌ Photo 2 capture failed:', result?.error);
            }
        } catch (error) {
            console.error('❌ Failed to capture photo 2:', error);
        } finally {
            setMiddlePhotoTriggered(false);
            setPhotoCaptureInProgress(prev => ({ ...prev, 2: false }));
        }
    };

    // ============================================
    // CAPTURE PHOTO 3 (End of Assessment) - SKIP IF ALREADY TAKEN
    // ============================================
    const capturePhoto3 = async () => {
        // Check if already taken
        const isTaken = await checkPhotoStatus(3);
        if (isTaken) {
            console.log('📸 Photo 3 already taken, skipping...');
            setFacePhotosTaken(prev => ({ ...prev, 3: true }));
            return;
        }

        if (photoCaptureInProgress[3]) {
            console.log('📸 Photo 3 capture already in progress');
            return;
        }

        try {
            setPhotoCaptureInProgress(prev => ({ ...prev, 3: true }));
            console.log('📸 Capturing photo 3 in background...');

            const result = await backgroundPhotoService.capturePhoto3(accessToken);

            if (result && result.success) {
                setFacePhotosTaken(prev => ({ ...prev, 3: true }));
                console.log('✅ Photo 3 captured successfully');
            } else {
                console.error('❌ Photo 3 capture failed:', result?.error);
            }
        } catch (error) {
            console.error('❌ Failed to capture photo 3:', error);
        } finally {
            setPhotoCaptureInProgress(prev => ({ ...prev, 3: false }));
        }
    };

    // ============================================
    // CHECK AND CAPTURE MIDDLE PHOTO (Photo 2 - Auto) - SKIP IF ALREADY TAKEN
    // ============================================
    const checkAndCaptureMiddlePhoto = async () => {
        console.log('🔍 checkAndCaptureMiddlePhoto called');
        console.log('facePhotosTaken[2]:', facePhotosTaken[2]);
        console.log('photoCaptureInProgress[2]:', photoCaptureInProgress[2]);
        console.log('middlePhotoTriggered:', middlePhotoTriggered);

        // Check if photo 2 is already taken
        const isTaken = await checkPhotoStatus(2);
        if (isTaken) {
            console.log('📸 Photo 2 already taken, skipping...');
            setFacePhotosTaken(prev => ({ ...prev, 2: true }));
            return false;
        }

        if (!facePhotosTaken[2] && !photoCaptureInProgress[2] && !middlePhotoTriggered) {
            console.log('📸 Triggering middle photo capture in background');
            // Don't await - let it run in background
            capturePhoto2();
            return true;
        }
        return false;
    };

    // ============================================
    // HANDLE MANUAL PHOTO 1 CAPTURE COMPLETE
    // ============================================
    const handleManualPhotoComplete = (result) => {
        console.log('✅ Manual photo 1 captured:', result);
        setFacePhotosTaken(prev => ({ ...prev, 1: true }));
        setShowManualPhotoCapture(false);
        // Proceed to test after photo 1 is captured
        initTest();
    };

    // ============================================
    // HANDLE SUBMIT WITH PHOTO 3 - SKIP IF ALREADY TAKEN
    // ============================================
    const handleSubmitWithPhoto3 = async () => {
        console.log('📸 Checking for photo 3 before submit');

        // Check if photo 3 is already taken
        const isTaken = await checkPhotoStatus(3);
        if (isTaken) {
            console.log('📸 Photo 3 already taken, skipping capture');
            setFacePhotosTaken(prev => ({ ...prev, 3: true }));
            await handleSubmitAssessment();
            return;
        }

        if (!facePhotosTaken[3] && !photoCaptureInProgress[3]) {
            // Capture photo 3 in background before submitting
            await capturePhoto3();
        }

        // Submit assessment
        await handleSubmitAssessment();
    };

    // ============================================
    // SESSION CHECK
    // ============================================
    const checkSessionAndProceed = async () => {
        try {
            const statusResponse = await api.get(`/sessions/${accessToken}/status`);

            if (statusResponse.data.success) {
                const session = statusResponse.data.session;
                console.log('📋 Session found:', session);

                const lang = session.language || session.default_language || 'en';
                setLanguageLocal(lang);
                setLanguage(lang);

                const detectedAssessmentId = session.assessment_id || 'MIPS-00001';
                const detectedFormId = session.demographics_form_id || null;

                setAssessmentId(detectedAssessmentId);
                setFormId(detectedFormId);

                let type = 'MIPS';
                if (detectedAssessmentId.startsWith('CPM-')) {
                    type = 'CPM';
                } else if (detectedAssessmentId.startsWith('GHQ-')) {
                    type = 'GHQ';
                } else if (detectedAssessmentId.startsWith('EBCSS-')) {
                    type = 'EBCSS';
                }

                setAssessmentType(type);

                if (session.demographics_completed) {
                    setDemographicsCompleted(true);
                    setShowDemographics(false);
                    checkSampleVideo();
                } else {
                    setShowDemographics(true);
                    setLoading(false);
                }
            } else {
                setError('Invalid session. Please check your invitation link.');
                setLoading(false);
            }
        } catch (error) {
            console.error('Error checking session:', error);
            setError('Unable to validate session. Please check your invitation link.');
            setLoading(false);
        }
    };

    // ============================================
    // SAMPLE VIDEO
    // ============================================
    const checkSampleVideo = async () => {
        try {
            setLoading(true);
            console.log('📹 Checking for sample video...');

            const response = await api.get(`/sessions/${accessToken}/sample-video`);

            if (response.data.success) {
                const video = response.data.sample_video;
                setSampleVideoData({
                    url: video.url,
                    required: video.required || false,
                    watched: video.watched || false
                });

                if (video.url && !video.watched) {
                    setShowSampleVideo(true);
                    setShowTest(false);
                    setLoading(false);
                    return;
                }
            }

            setLoading(false);
            proceedToManualPhoto();
        } catch (error) {
            console.log('No sample video configured:', error.message);
            setLoading(false);
            proceedToManualPhoto();
        }
    };

    const handleVideoComplete = () => {
        setVideoCompleted(true);
        setShowSampleVideo(false);
        proceedToManualPhoto();
    };

    // ============================================
    // PROCEED TO MANUAL PHOTO 1 - SKIP IF ALREADY TAKEN
    // ============================================
    const proceedToManualPhoto = async () => {
        if (faceVerificationEnabled) {
            // Check if photo 1 is already taken
            const isPhoto1Taken = await checkPhotoStatus(1);
            if (isPhoto1Taken) {
                console.log('📸 Photo 1 already taken, skipping manual capture');
                setFacePhotosTaken(prev => ({ ...prev, 1: true }));
                // Proceed directly to test
                initTest();
            } else {
                // Show manual photo capture for photo 1
                setShowManualPhotoCapture(true);
                setLoading(false);
            }
        } else {
            initTest();
        }
    };

    // ============================================
    // DEMOGRAPHICS
    // ============================================
    const handleDemographicsComplete = () => {
        setShowDemographics(false);
        setDemographicsCompleted(true);
        checkSampleVideo();
    };

    // ============================================
    // SUBMIT ASSESSMENT
    // ============================================
    const handleSubmitAssessment = async () => {
        if (isSubmitting) return;   // ← prevents double submit
        setIsSubmitting(true);
        try {
            setLoading(true);
            const response = await submitAssessment(accessToken);
            navigate(`/results/${accessToken}`, { state: { report: response.data.report } });
        } catch (err) {
            console.error('Error submitting assessment:', err);
            showError('Error submitting assessment. Please try again.');
            setLoading(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ============================================
    // FACE VERIFICATION HANDLERS
    // ============================================
    const handleVerificationComplete = (success) => {
        console.log('🔐 Verification complete:', success);
    };

    const handleTestPaused = (reason) => {
        console.log('⏸️ Test paused:', reason);
    };

    const handleFaceVerified = () => {
        console.log('✅ Face verified successfully!');
    };

    // ============================================
    // INIT TEST
    // ============================================
    const initTest = async () => {
        console.log('🚀 initTest called');
        setLoading(true);
        try {
            // Check face photo status
            try {
                const statusResponse = await api.get(`/face-photos/${accessToken}/status`);
                if (statusResponse.data.success) {
                    const status = statusResponse.data.status;
                    console.log('📸 Face photo status:', status);
                    setFacePhotosTaken({
                        1: status.face_photo_1_taken || false,
                        2: status.face_photo_2_taken || false,
                        3: status.face_photo_3_taken || false
                    });

                    if (status.face_photo_3_taken) {
                        setError('This assessment has already been completed.');
                        setLoading(false);
                        return;
                    }
                }
            } catch (error) {
                console.log('Could not fetch face photo status:', error.message);
            }

            // Check for saved progress
            try {
                const progressResponse = await getProgress(accessToken);
                if (progressResponse.data.success && progressResponse.data.progress) {
                    const progress = progressResponse.data.progress;
                    if (progress.answers && Object.keys(progress.answers).length > 0) {
                        console.log('📂 Found saved progress:', progress);
                        setSavedProgress(progress);
                    }
                }
            } catch (error) {
                console.log('No saved progress found:', error.message);
            }

            const response = await startSession(accessToken);
            setTestData(response.data);

            if (response.data.session?.assessment_id) {
                const detectedId = response.data.session.assessment_id;
                setAssessmentId(detectedId);
                if (detectedId.startsWith('CPM-')) {
                    setAssessmentType('CPM');
                } else if (detectedId.startsWith('GHQ-')) {
                    setAssessmentType('GHQ');
                } else if (detectedId.startsWith('EBCSS-')) {
                    setAssessmentType('EBCSS');
                } else {
                    setAssessmentType('MIPS');
                }
            }

            setShowTest(true);
            setLoading(false);
            console.log('✅ Test initialized successfully');
        } catch (err) {
            console.error('Error starting test:', err);
            setError('Unable to load assessment. Please check your link.');
            setLoading(false);
        }
    };

    // ============================================
    // CPM IMAGE HANDLING
    // ============================================
    const getCPMImagePath = (itemId) => {
        const imageId = itemId.replace('CPM-', '').trim();
        return `/images/cpm/${imageId}.png`;
    };

    const renderCPMOptions = (item, selectedOption, handleOptionSelect, answers) => {
        const options = ['1', '2', '3', '4', '5', '6'];
        return (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                {options.map((opt) => (
                    <button
                        key={opt}
                        onClick={() => handleOptionSelect(item.id, opt)}
                        className={`
                            p-4 rounded-lg border-2 transition-all duration-200
                            ${selectedOption === opt || answers[item.id] === opt
                                ? 'border-blue-600 bg-blue-50 shadow-md'
                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            }
                        `}
                    >
                        <div className="text-center">
                            <div className="text-xl font-bold text-gray-700">{opt}</div>
                        </div>
                    </button>
                ))}
            </div>
        );
    };

    const renderCPMImage = (itemId) => {
        const imagePath = getCPMImagePath(itemId);
        return (
            <div className="mb-6 flex justify-center">
                <img
                    src={imagePath}
                    alt={`Pattern ${itemId}`}
                    className="max-w-full max-h-96 object-contain border border-gray-200 rounded-lg"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        const parent = e.target.parentElement;
                        const fallback = document.createElement('div');
                        fallback.className = 'w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300';
                        fallback.innerHTML = `
                            <div class="text-center">
                                <p class="text-gray-500">Image not available</p>
                                <p class="text-sm text-gray-600">${itemId}</p>
                            </div>
                        `;
                        parent.appendChild(fallback);
                    }}
                />
            </div>
        );
    };

    // ============================================
    // RENDER
    // ============================================
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff9f2f] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
                <div className="text-center text-red-600">
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (showDemographics) {
        return (
            <DemographicsForm
                accessToken={accessToken}
                onComplete={handleDemographicsComplete}
                assessmentId={assessmentId}
                formId={formId}
            />
        );
    }

    if (showSampleVideo && sampleVideoData.url) {
        return (
            <div className="min-h-screen bg-[#f4f6fb] py-12">
                <div className="max-w-3xl mx-auto px-4">
                    <SampleVideo
                        accessToken={accessToken}
                        onComplete={handleVideoComplete}
                        videoUrl={sampleVideoData.url}
                        isWatched={sampleVideoData.watched}
                        assessmentId={assessmentId}
                    />
                </div>
            </div>
        );
    }

    // Manual Photo 1 Capture
    if (showManualPhotoCapture) {
        return (
            <div className="min-h-screen bg-[#f4f6fb] py-12">
                <div className="max-w-2xl mx-auto px-4">
                    <ManualFaceCapture
                        accessToken={accessToken}
                        onCaptureComplete={handleManualPhotoComplete}
                        photoOrder={1}
                        title="Take Your Photo"
                        subtitle="Please take a photo before starting the assessment"
                    />
                </div>
            </div>
        );
    }

    if (showTest && testData) {
        const handleSubmitWithCheck = async () => {
            await handleSubmitWithPhoto3();
        };

        return (
            <>
                <TestInterface
                    accessToken={accessToken}
                    items={testData.items}
                    config={testData.config}
                    onSubmit={handleSubmitWithCheck}
                    isCPM={assessmentType === 'CPM'}
                    assessmentType={assessmentType}
                    renderCPMImage={renderCPMImage}
                    renderCPMOptions={renderCPMOptions}
                    savedProgress={savedProgress}
                    language={language}
                    t={t}
                    onMiddlePhotoNeeded={checkAndCaptureMiddlePhoto}
                    facePhoto2Taken={facePhotosTaken[2]}
                />
                {faceVerificationEnabled && (
                    <FaceVerification
                        accessToken={accessToken}
                        onVerificationComplete={handleVerificationComplete}
                        onTestPaused={handleTestPaused}
                        onFaceVerified={handleFaceVerified}
                    />
                )}
            </>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
            <p className="text-gray-600">Loading assessment...</p>
        </div>
    );
};

export default TestPage;